import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { PortalOptionModel } from '@models/types'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCreateOrEditPortalOptionModal from './hooks/useCreateOrEditPortalOptionModal'
import NodeProperties from './NodeProperties'

const MODAL_KEY = 'CreateOrEditPortalOptionModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPortalOption: (payload: HandleCallbackActionParams) => Promise<object>,
  updatePortalOption: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditPortalOptionModalProps = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<PortalOptionModel>,
  updateFn: Function,
}

const createOrEditPortalOption = (params: CreateOrEditPortalOptionModalProps) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createPortalOption, updatePortalOption } = callbacks || {}

  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const portalOptionPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Portal Option ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updatePortalOption(portalOptionPayload)

  // @ts-expect-error TS2345
  return createPortalOption(portalOptionPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditPortalOptionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { portalOption } = modalPayload

  const {
    callbacks: {
      toggleNodeProperty,
      createPortalOption: createFn,
      deletePortalOption: deleteFn,
      updatePortalOption: updateFn,
    },
    nodePropertyFormPayload,
    portalOptionFormPayload,
    loading,
  } = useCreateOrEditPortalOptionModal(portalOption)

  const {
    entityState,
    entityState: {
      id,
      node_name,
      node_properties,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = portalOptionFormPayload

  const updateOrCreateButtonText = id ? 'Save Portal Option' : 'Create Portal Option'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Portal Option" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Node Name" errorMessage={errors.node_name} marginTop="large">
            <input name="node_name" type="text" value={node_name} {...handlers} />
          </Form.Field>

          <NodeProperties
            callbacks={{ toggleNodeProperty }}
            nodeProperties={node_properties || []}
            nodePropertyFormPayload={nodePropertyFormPayload}
          />

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryEdit"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                // @ts-expect-error
                marginRight="medium"
                onClick={() => confirmDelete({
                  callbacks: { afterAction: closeModal, deleteFn },
                  strings: { entityName: 'Portal Option' },
                })}
                size="medium"
              >
                Delete Portal Option
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={loading}
          onClick={() => createOrEditPortalOption({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPortalOptionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
